<template>
  <div class="card-form">
    <div id="card-number"></div>
    <div class="flex">
      <div id="card-expiry"></div>
      <div id="card-cvc"></div>
    </div>
    <div id="card-error"></div>
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>処理中です...</p>
    </div>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js'
import {
  clearYagiprimePurchasePending,
  trackYagiprimePurchase
} from '@/composables/useYagiprimePurchaseTracking'

export default {
  props: {
    onValidationChange: {
      type: Function,
      required: true
    },
    isSubscription: {
      type: Boolean,
      default: false
    },
    amount: {
      type: Number,
      default: 550
    },
    selectedIndex: {
      type: Number,
      default: 1
    },
    payment_method_used: {
      type: String,
      default: ''
    },
    print_type: {
      type: String,
      default: ''
    },
    format_id: {
      type: String,
      default: ''
    },
    notificationSetting: {
      type: String,
      default: 'on'
    },
    paymentMode: {
      type: String,
      default: 'eprint',
      validator: function (v) {
        return ['eprint', 'quickemail'].indexOf(v) !== -1
      }
    },
    quickemailFormData: {
      type: Object,
      default: function () { return {} }
    }
  },
  data () {
    return {
      stripe: null,
      elements: null,
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
      token: null,
      isCardNumberComplete: false,
      isCardExpiryComplete: false,
      isCardCvcComplete: false,
      isLoading: false,
      isProcessing: false, // 重複決済防止フラグ
      isAlreadySubscribed: false, // 409受信時にブロック（既にサブスク存在）
      paymentIntent: null,
      quickemailHistoryId: null
    }
  },
  computed: {
    isFormValid () {
      return this.isCardNumberComplete && this.isCardExpiryComplete && this.isCardCvcComplete
    }
  },
  watch: {
    isFormValid (newValue) {
      // 親コンポーネントに状態を通知
      this.onValidationChange(newValue)
    }
  },
  async mounted () {
    try {
      this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
      if (!this.stripe) {
        console.error('Stripeの初期化に失敗しました。公開鍵を確認してください。')
        return
      }

      this.elements = this.stripe.elements()

      const style = {}

      this.cardNumber = this.elements.create('cardNumber', { style })
      this.cardNumber.mount('#card-number')
      this.cardNumber.on('change', this.handleCardChange)

      this.cardExpiry = this.elements.create('cardExpiry', { style })
      this.cardExpiry.mount('#card-expiry')
      this.cardExpiry.on('change', this.handleCardChange)

      this.cardCvc = this.elements.create('cardCvc', { style })
      this.cardCvc.mount('#card-cvc')
      this.cardCvc.on('change', this.handleCardChange)
    } catch (error) {
      console.error('初期化中にエラーが発生しました:', error)
    }
  },
  beforeDestroy () {
    if (this.cardNumber) this.cardNumber.destroy()
    if (this.cardExpiry) this.cardExpiry.destroy()
    if (this.cardCvc) this.cardCvc.destroy()
  },
  methods: {
    handleCardChange (event) {
      if (event.elementType === 'cardNumber') {
        this.isCardNumberComplete = event.complete
      } else if (event.elementType === 'cardExpiry') {
        this.isCardExpiryComplete = event.complete
      } else if (event.elementType === 'cardCvc') {
        this.isCardCvcComplete = event.complete
      }

      if (event.error) {
        document.getElementById('card-error').innerHTML = event.error.message
      } else {
        document.getElementById('card-error').innerHTML = ''
      }
    },
    async createToken () {
      console.log('トークンを作成します...')

      // 重複処理防止チェック
      if (this.isProcessing) {
        console.log('既に処理中のため、トークン作成をスキップします')
        return
      }

      try {
        const { token, error } = await this.stripe.createToken(this.cardNumber)
        if (error) {
          document.getElementById('card-error').innerHTML = error.message
          console.error('トークン作成エラー:', error.message)
          this.$emit('payment-error', { message: error.message })
          return
        }
        this.token = token.id
        console.log('トークン生成成功:', token)
        this.tokenCreated(token)
      } catch (error) {
        console.error('トークン作成中にエラーが発生しました:', error)
        this.$emit('payment-error', { message: error.message || 'トークン作成中にエラーが発生しました' })
      }
    },
    async createSubscription (token) {
      console.log('[サブスクリプション] 作成開始')
      console.log('[サブスクリプション] トークン:', token.id)

      // 重複決済防止チェック
      if (this.isProcessing) {
        console.log('既に決済処理中のため、サブスクリプション作成をスキップします')
        return
      }

      this.isProcessing = true
      this.isLoading = true
      this.$eventHub.$emit('loading_on')
      this.token = ''
      if (token) {
        this.token = token.id
      }

      try {
        console.log('[サブスクリプション] サブスクリプション作成開始')
        const formData = new FormData()
        formData.append('stripe_token', this.token)
        formData.append('uuid', this.$firebase.user.uid)
        formData.append('subscription_type', this.selectedIndex)
        formData.append('notification_setting', this.notificationSetting)
        formData.append('app_flag', this.$site.appFlag)
        formData.append('device_flg', this.$deviceLib.isMobileDevice ? 'SP' : 'PC')
        formData.append('version', '3')
        formData.append('site_flag', 'r')
        formData.append('payment_method', 'card')

        console.log('[サブスクリプション] 作成リクエスト送信:', {
          subscription_type: 1,
          uuid: this.$firebase.user.uid
        })

        const response = await this.$axios.post(this.$varuna.endPoints.subscription, formData, { timeout: 60000 })
        console.log('[サブスクリプション] 作成成功:', response.data)

        if (response.data.requires_action) {
          console.log('[サブスクリプション] 3Dセキュア認証が必要')
          const { error: confirmError } = await this.stripe.confirmCardPayment(
            response.data.payment_intent_client_secret,
            {
              payment_method: {
                card: this.cardNumber
              },
              return_url: window.location.href
            }
          )

          if (confirmError) {
            console.error('[サブスクリプション] 3Dセキュア認証エラー:', confirmError)
            throw confirmError
          }

          console.log('[サブスクリプション] 3Dセキュア認証成功、サブスクリプションを確認')
          const confirmFormData = new FormData()
          confirmFormData.append('payment_intent_id', response.data.payment_intent_id)
          confirmFormData.append('stripe_subscription_id', response.data.stripe_subscription_id)
          confirmFormData.append('uuid', this.$firebase.user.uid)

          console.log('[サブスクリプション] 確認リクエスト送信:', {
            payment_intent_id: response.data.payment_intent_id,
            stripe_subscription_id: response.data.stripe_subscription_id
          })

          const confirmResponse = await this.$axios.post(this.$varuna.endPoints.confirmSubscription, confirmFormData)
          console.log('[サブスクリプション] 確認レスポンス:', confirmResponse.data)

          // レスポンスの成功チェック
          if (confirmResponse.status !== 200 || (confirmResponse.data && confirmResponse.data.success === false)) {
            console.error('[サブスクリプション] 確認失敗:', confirmResponse)
            const errorMessage = (confirmResponse.data && confirmResponse.data.message) ? confirmResponse.data.message : 'サブスクリプションの確認に失敗しました'
            const error = new Error(errorMessage)
            error.response = confirmResponse
            throw error
          }

          console.log('[サブスクリプション] 確認処理完了 - データベース更新済み')
        }

        if (response.status === 201 || response.status === 200) {
          console.log('[サブスクリプション] ステータス201、エントリーソース設定を開始')
          const subformData = new FormData()

          const routeId = this.$cookies.get('_YAGISH_YAGIPRIME_ROUTE_ID')
          if (routeId) {
            subformData.append('route', routeId)
          } else {
            subformData.append('route', '')
          }
          subformData.append('uuid', this.$firebase.user.uid)

          console.log('[サブスクリプション] エントリーソース設定リクエスト送信:', {
            route: routeId || '',
            uuid: this.$firebase.user.uid
          })

          await this.$axios.post(this.$varuna.endPoints.setYagiprimeEntrySource, subformData, { timeout: 60000 })
          console.log('[サブスクリプション] エントリーソース設定成功')
          // 課金確定点。入口（main / font / quickemail）に関わらずここを通るので purchase はここで送る
          trackYagiprimePurchase(this.$gtm, {
            transactionId:
              response.data.stripe_subscription_id ||
              response.data.payment_intent_id,
            uuid: this.$firebase.user.uid
          })
          this.$eventHub.$emit('payment_yagiprime_result', 'success')
          // 親コンポーネントにも成功を通知
          this.$emit('payment-success', { type: 'subscription', status: 'success' })
        }
      } catch (error) {
        console.error('[サブスクリプション] 作成失敗:', error)
        console.error('[サブスクリプション] エラー詳細:', {
          message: error.message,
          response: error.response ? error.response.data : 'レスポンスなし',
          status: error.response ? error.response.status : 'ステータスなし',
          code: error.code || 'コードなし'
        })
        let errorMessage = 'サブスクリプション作成に失敗しました'

        // 409エラー（重複サブスクリプション）の場合
        if (error.response && error.response.status === 409) {
          console.log('[サブスクリプション] 409エラー - 既存のサブスクリプションが存在します')
          console.log('[サブスクリプション] 409エラー詳細:', error.response.data)

          this.isAlreadySubscribed = true
          // 既存加入は購入ではないので purchase を送らず、文脈だけ捨てる
          clearYagiprimePurchasePending()
          // 削除処理は行わず、既存として扱う
          this.$eventHub.$emit('payment_yagiprime_result', 'success')
          this.$emit('payment-success', { type: 'subscription', status: 'already_exists' })
          return
        }

        // 400エラー（決済失敗）の場合
        if (error.response && error.response.status === 400) {
          console.log('[サブスクリプション] 400エラー - 決済処理失敗')
          console.log('[サブスクリプション] 400エラー詳細:', error.response.data)

          if (error.response.data && error.response.data.error_type === 'payment_failed') {
            errorMessage = error.response.data.message || 'カード決済に失敗しました。カード情報を確認してください。'
          }
        }

        // 500エラー（サーバーエラー）の場合
        if (error.response && error.response.status === 500) {
          console.log('[サブスクリプション] 500エラー - サーバーエラー')
          console.log('[サブスクリプション] 500エラー詳細:', error.response.data)
          errorMessage = '決済処理中にエラーが発生しました。しばらく待ってから再度お試しください。'
        }

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }

        // エラーメッセージを含むイベントを発行
        this.$eventHub.$emit('payment_yagiprime_result', { status: 'error', message: errorMessage })
        this.$emit('payment-error', { message: errorMessage })
      } finally {
        console.log('[サブスクリプション] 処理完了')
        this.isLoading = false
        if (!this.isAlreadySubscribed) {
          this.isProcessing = false // 処理完了フラグをリセット（409受信時はリセットしない）
        }
        this.$eventHub.$emit('loading_off')
      }
    },
    tokenCreated (token) {
      if (this.isSubscription) {
        this.createSubscription(token)
      } else {
        this.createPayment(token)
      }
    },

    async createPayment (token) {
      console.log('[決済] 処理開始')
      console.log('[決済] トークン:', token.id)
      // 前回決済の残骸をクリア (mode切替/リトライ時に古い ID が emit/記録されるのを防ぐ)
      this.quickemailHistoryId = null
      this.paymentIntent = null
      try {
        this.isLoading = true

        // Payment Intentを作成
        const paymentIntent = await this.createPaymentIntent()
        console.log('[決済] Payment Intent作成完了:', paymentIntent)

        // 3Dセキュア認証を含む決済処理を実行
        console.log('[決済] confirmCardPayment実行開始')
        console.log('[決済] client_secret:', paymentIntent.client_secret)
        console.log('[決済] カード要素:', this.cardNumber)

        const { error: confirmError, paymentIntent: confirmedPaymentIntent } = await this.stripe.confirmCardPayment(
          paymentIntent.client_secret,
          {
            payment_method: {
              card: this.cardNumber,
              billing_details: {
                name: this.$firebase.user.uid,
                email: this.$firebase.user.email
              }
            },
            return_url: window.location.href
          }
        )

        console.log('[決済] confirmCardPayment実行完了')
        console.log('[決済] エラー:', confirmError)
        console.log('[決済] PaymentIntent:', confirmedPaymentIntent)

        if (confirmError) {
          console.error('[決済] 認証エラー:', confirmError)
          console.error('[決済] エラータイプ:', confirmError.type)
          console.error('[決済] エラーコード:', confirmError.code)

          // Stripeエラーメッセージを日本語化
          let errorMessage = confirmError.message
          if (confirmError.code === 'insufficient_funds') {
            errorMessage = 'カードの残高が不足しています。別のカードをお試しください。'
          } else if (confirmError.code === 'card_declined') {
            errorMessage = 'カードが拒否されました。カード会社にお問い合わせください。'
          } else if (confirmError.code === 'expired_card') {
            errorMessage = 'カードの有効期限が切れています。'
          } else if (confirmError.code === 'incorrect_cvc') {
            errorMessage = 'セキュリティコード（CVC）が正しくありません。'
          } else if (confirmError.code === 'processing_error') {
            errorMessage = '決済処理中にエラーが発生しました。しばらく時間をおいて再度お試しください。'
          }

          // エラーメッセージを更新してthrow
          const enhancedError = new Error(errorMessage)
          enhancedError.code = confirmError.code
          enhancedError.type = confirmError.type
          throw enhancedError
        }

        console.log('[決済] 決済完了:', confirmedPaymentIntent)
        console.log('[決済] 最終ステータス:', confirmedPaymentIntent.status)

        // 決済ログを保存してからpayment-successを発火
        // quickemailモード: webhook主導でサーバー側がDB保存するため、フロントからのlog保存はスキップ
        let paymentHistoryId = null
        if (this.paymentMode !== 'quickemail') {
          try {
            paymentHistoryId = await this.savePaymentLogWithStripeId(confirmedPaymentIntent.id)
            console.log('[決済] 決済ログ保存成功, paymentHistoryId:', paymentHistoryId)
          } catch (logError) {
            console.error('[決済] 決済ログ保存失敗:', logError)
            // ログ保存失敗でも決済は成功しているので続行
          }
        }

        // 決済成功時は、トークンと一緒に成功を通知
        // quickemailHistoryId は webhook主導モード時のみ含める (eprint モード混同防止)
        this.$emit('payment-success', {
          type: 'single',
          token,
          paymentIntent: confirmedPaymentIntent,
          paymentHistoryId,
          paymentIntentId: confirmedPaymentIntent.id,
          quickemailHistoryId: this.paymentMode === 'quickemail' ? this.quickemailHistoryId : null
        })
      } catch (error) {
        console.error('[決済] 処理失敗:', error)
        console.error('[決済] エラー詳細:', {
          message: error.message,
          response: error.response ? error.response.data : 'レスポンスなし',
          status: error.response ? error.response.status : 'ステータスなし',
          code: error.code || 'コードなし'
        })

        let errorMessage = '決済処理に失敗しました'
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }

        // ユーザーへの失敗通知を先に行い、ログ保存待ちでUIを遅延させない
        this.$emit('payment-error', { message: errorMessage })

        // 失敗ログをDBに記録 (quickemailモードはwebhook主導なのでスキップ)
        // PaymentIntent未生成のステップで落ちた場合は 'unknown' を入れ、どの段階の失敗か追える
        // fire-and-forget: savePaymentLogWithStripeId は内部で例外を握りつぶす
        if (this.paymentMode !== 'quickemail') {
          const failedIntentId =
            (this.paymentIntent && this.paymentIntent.id) ||
            (error.payment_intent && error.payment_intent.id) ||
            'unknown'
          this.savePaymentLogWithStripeId(failedIntentId, {
            status: false,
            errorCode: error.code || 'unknown',
            errorMessage: errorMessage
          })
        }
      } finally {
        this.isLoading = false
      }
    },
    async createPaymentIntent () {
      // quickemailモード: draft先行作成エンドポイントを呼び全フォームデータを送信する
      // webhook主導でメール送信されるため、後続の log_convenience_payment / payment_quickemail は不要
      if (this.paymentMode === 'quickemail') {
        return this.createQuickmailIntent()
      }

      console.log('[PaymentIntent] 作成開始')
      console.log('[PaymentIntent] リクエストデータ:', {
        uuid: this.$firebase.user.uid,
        amount: this.amount,
        currency: 'jpy'
      })
      try {
        const formData = new FormData()
        formData.append('uuid', this.$firebase.user.uid)
        formData.append('amount', this.amount)
        formData.append('currency', 'jpy')
        // eprint後課金は authorize+capture 分離（与信枠だけ確保→番号発行成功後にサーバー側でcapture）。
        // これによりコンテンツ番号の発行に失敗した場合は課金されない（PayPayと同じ「発行→課金→開示」順）。
        if (this.paymentMode === 'eprint') {
          formData.append('capture_method', 'manual')
        }

        const response = await this.$axios.post(this.$varuna.endPoints.createPaymentIntent, formData)
        console.log('[PaymentIntent] 作成成功:', response.data)
        console.log('[PaymentIntent] ステータス:', response.data.status)
        console.log('[PaymentIntent] ID:', response.data.id)
        this.paymentIntent = response.data
        return this.paymentIntent
      } catch (error) {
        console.error('[PaymentIntent] 作成失敗:', error)
        console.error('[PaymentIntent] エラー詳細:', {
          message: error.message,
          response: error.response ? error.response.data : 'レスポンスなし',
          status: error.response ? error.response.status : 'ステータスなし',
          code: error.code || 'コードなし'
        })

        let errorMessage = '決済処理の開始に失敗しました'
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }

        this.$emit('payment-error', { message: errorMessage })
        throw error
      }
    },
    async createQuickmailIntent () {
      console.log('[QuickmailIntent] draft作成開始')
      try {
        const formData = new FormData()
        formData.append('uuid', this.$firebase.user.uid)
        formData.append('version', '4')
        formData.append('payment_method', 'card')
        formData.append('stripe_token', '')
        formData.append('merchant_payment_id', '')

        Object.keys(this.quickemailFormData).forEach((key) => {
          const value = this.quickemailFormData[key]
          if (value === null || value === undefined) {
            return
          }
          formData.append(key, value)
        })

        const response = await this.$axios.post(
          this.$varuna.endPoints.createQuickmailPaymentIntent,
          formData,
          { timeout: 60000 }
        )

        if (!response.data || response.data.status !== 'success') {
          throw new Error(
            (response.data && response.data.message) || 'メール便用 PaymentIntent の作成に失敗しました'
          )
        }

        console.log('[QuickmailIntent] 成功:', {
          paymentIntentId: response.data.paymentIntentId,
          history_id: response.data.history_id
        })

        this.quickemailHistoryId = response.data.history_id
        this.paymentIntent = {
          client_secret: response.data.clientSecret,
          id: response.data.paymentIntentId
        }
        return this.paymentIntent
      } catch (error) {
        console.error('[QuickmailIntent] 失敗:', error)
        let errorMessage = '決済処理の開始に失敗しました'
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }
        this.$emit('payment-error', { message: errorMessage })
        throw error
      }
    },
    async savePaymentLogWithStripeId (paymentIntentId, options = {}) {
      // status: 決済成否 (default true で後方互換)、失敗時は errorCode/errorMessage を detail に同梱
      const { status = true, errorCode = null, errorMessage = null } = options
      const paymentStatus = status ? 'true' : 'false'
      console.log('[決済ログ] Stripe決済ID付きで保存開始:', paymentIntentId, 'status:', paymentStatus)
      try {
        const formData = new FormData()
        formData.append('uuid', this.$firebase.user.uid)
        formData.append('payment_type', 'stripe')
        formData.append('payment_cost', this.amount)
        formData.append('payment_status', paymentStatus)
        formData.append('print_type', this.print_type || '0')
        formData.append('format_id', this.format_id || '')
        // Stripe決済IDをdetailに保存 (失敗時はエラー情報も付与)
        const detailObj = {
          payment_intent_id: paymentIntentId,
          timestamp: new Date().toISOString()
        }
        if (!status) {
          detailObj.error_code = errorCode
          detailObj.error_message = errorMessage
        }
        formData.append('detail', JSON.stringify(detailObj))

        console.log('[決済ログ] 保存データ:', {
          uuid: this.$firebase.user.uid,
          payment_type: 'stripe',
          payment_cost: this.amount,
          payment_status: paymentStatus,
          print_type: this.print_type || '0',
          format_id: this.format_id || '',
          detail: `payment_intent_id: ${paymentIntentId}`
        })

        const response = await this.$axios.post(this.$varuna.endPoints.logConveniencePayment, formData, { timeout: 30000 })
        console.log('[決済ログ] 保存成功:', response.data)

        return response.data.payment_history_id
      } catch (error) {
        console.error('[決済ログ] 保存失敗:', {
          message: error.message,
          response: error.response && error.response.data,
          status: error.response && error.response.status
        })
        // ログ保存失敗は決済処理には影響させない
      }
    }
  }
}
</script>

<style>
/*クレカフォーム*/
.card-form {
  position: relative;
}
.card-form .StripeElement {
  background: #fff;
  border: 1px solid #dfdfdf;
  border-radius: 10px;
  margin: 10px 0;
  padding: 12px;
  margin-bottom: 10px;
}
.card-form .flex .StripeElement {
  width: calc(50% - 5px);
}
.card-form .flex {
  display: flex;
  justify-content: space-between;
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
